import { GarnerProvidersClientV1 } from '@garner-health/api-client/svc-providers/providers/providers-v1';
import { AsyncResult, failure, objectToCamel, success } from '@garner-health/lib-ts-common';

import createLogger from '~/logging';
import { Position } from '~/util';

import { baseClientOptions, demoClientOptions, getDefaultHeaders, getRateLimitReset } from './common';
import { Hours, Metric } from './types';

const log = createLogger(__filename);

type ProfessionalNetwork = {
  networkId: string;
  isInNetwork: boolean;
  isTopProvider: boolean;
};

type ProfessionalSpecialtyLocation = {
  locationId: string;
  networks: ProfessionalNetwork[];
};

export type ProfessionalSpecialty = {
  overallScore: number;
  locations: ProfessionalSpecialtyLocation[];
};

export type LocationBase = {
  lines: string[];
  city: string;
  state: string;
  zipCode: string;
  position: Position;
  hours?: Hours[];
  phoneNumber?: string;
  faxNumber?: string;
};

export type ProfessionalLocation = LocationBase & {
  name?: string;
  acceptsNewPatients?: boolean;
  facilityName?: string;
  providerCorporationName?: string;
  allProviderCorporations?: string[];
};

export type Professional = {
  id: string;
  npi: string;
  firstName: string;
  lastName: string;
  credentials?: string;
  reviewStars?: number;
  sex: 'female' | 'male';
  languages?: string[];
  metrics: Metric[];
  specialties: Map<string, ProfessionalSpecialty>;
  locations: Map<string, ProfessionalLocation>;
};

type FacilityNetwork = {
  networkId: string;
  isInNetwork: boolean;
};

type FacilitySpecialty = {
  networks: FacilityNetwork[];
};

export type FacilityLocation = LocationBase & {
  locationId: string;
};

export type Facility = {
  id: string;
  npi: string;
  organizationName: string;
  specialties: Map<string, FacilitySpecialty>;
  location: FacilityLocation;
};

export type ProviderFailureReason = 'notFound' | 'rateLimited';

export class ProviderClient {
  readonly client: GarnerProvidersClientV1;
  readonly demoClient: GarnerProvidersClientV1;

  constructor(options = baseClientOptions, demoOptions = demoClientOptions) {
    this.client = new GarnerProvidersClientV1(options);
    this.demoClient = new GarnerProvidersClientV1(demoOptions);
  }

  async getProfessional({
    providerId,
    networkId,
    isDemo,
  }: {
    providerId: string;
    networkId: string;
    isDemo: boolean;
  }): AsyncResult<Professional, ProviderFailureReason> {
    return await (isDemo ? this.demoClient : this.client)
      .get('/professionals/{professional_id}', {
        headers: await getDefaultHeaders(),
        params: { professional_id: providerId },
        query: { network_id: [networkId] },
      })
      .onStatus(200, r => {
        const specialtyMap = new Map();
        const locationMap = new Map();
        for (const key in r.json.specialties) {
          specialtyMap.set(key, objectToCamel(r.json.specialties[key]));
        }
        for (const key in r.json.locations) {
          locationMap.set(key, objectToCamel(r.json.locations[key]));
        }
        return success({
          ...objectToCamel(r.json),
          metrics: r.json.metrics ?? [],
          specialties: specialtyMap,
          locations: locationMap,
        });
      })
      .onStatus(404, err => {
        log.error({ message: err.json.message, providerId, networkId }, 'Professional not found');
        return failure('notFound');
      })
      .onStatus(422, err => {
        log.error({ message: err.json.message, providerId, networkId }, 'Bad request when getting professional');
        return failure('notFound');
      })
      .onStatus(429, err => {
        log.warn(
          { providerId, networkId, strRateLimitReset: getRateLimitReset(err.headers) },
          'Rate limited when getting professional',
        );
        return failure('rateLimited');
      })
      .catch(err => {
        log.error({ err, providerId, networkId }, 'Error getting professional');
        throw err;
      });
  }

  async getFacility({
    providerId,
    locationId,
    networkId,
    isDemo,
  }: {
    providerId: string;
    locationId: string;
    networkId: string;
    isDemo: boolean;
  }): AsyncResult<Facility, ProviderFailureReason> {
    return await (isDemo ? this.demoClient : this.client)
      .get('/facilities/{facility_id}', {
        headers: await getDefaultHeaders(),
        params: { facility_id: providerId },
        query: { location_id: locationId, network_id: [networkId] },
      })
      .onStatus(200, r => {
        const specialtyMap = new Map();
        for (const key in r.json.specialties) {
          specialtyMap.set(key, objectToCamel(r.json.specialties[key]));
        }
        return success({
          ...objectToCamel(r.json),
          npi: r.json.npi!, // TODO: remove this assertion when this ticket is addressed: https://app.asana.com/0/1203374383582495/1205452174479088/f
          specialties: specialtyMap,
        });
      })
      .onStatus(404, err => {
        log.error({ message: err.json.message, providerId, networkId }, 'Facility not found');
        return failure('notFound');
      })
      .onStatus(422, err => {
        log.error({ message: err.json.message, providerId, networkId }, 'Bad request when getting facility');
        return failure('notFound');
      })
      .onStatus(429, err => {
        log.warn(
          { providerId, networkId, strRateLimitReset: getRateLimitReset(err.headers) },
          'Rate limited when getting facility',
        );
        return failure('rateLimited');
      })
      .catch(err => {
        log.error({ err, providerId, networkId }, 'Error getting facility');
        throw err;
      });
  }
}

export const providerClient = new ProviderClient();
